import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Loader2, Save, ShoppingCart, Crown, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCheckoutConfig = () => {
  const [config, setConfig] = useState({ modules: {}, bundles: {} });
  const [courseTitles, setCourseTitles] = useState({});
  const [bundleTitles, setBundleTitles] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | available | soon

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cfgRes, coursesRes, bundlesRes] = await Promise.all([
        axios.get(`${API}/admin/checkout-config`),
        axios.get(`${API}/courses`),
        axios.get(`${API}/bundles`),
      ]);
      setConfig({
        modules: cfgRes.data.modules || {},
        bundles: cfgRes.data.bundles || {},
      });
      const ct = {};
      coursesRes.data.forEach((c) => { ct[String(c.id)] = c.title; });
      setCourseTitles(ct);
      const bt = {};
      bundlesRes.data.forEach((b) => { bt[String(b.id)] = b.title; });
      setBundleTitles(bt);
    } catch (err) {
      toast.error('Erro ao carregar configuração de checkout.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const setModuleField = (id, field, value) => {
    setConfig((c) => ({ ...c, modules: { ...c.modules, [id]: { ...c.modules[id], [field]: value } } }));
  };
  const setBundleField = (id, field, value) => {
    setConfig((c) => ({ ...c, bundles: { ...c.bundles, [id]: { ...c.bundles[id], [field]: value } } }));
  };

  const saveModule = async (id) => {
    setSavingKey(`m-${id}`);
    try {
      const m = config.modules[id];
      await axios.put(`${API}/admin/checkout-config/module/${id}`, {
        offer: m.offer || '',
        available: !!m.available,
      });
      toast.success(`Módulo ${id} salvo.`);
    } catch {
      toast.error('Erro ao salvar módulo.');
    } finally {
      setSavingKey(null);
    }
  };

  const saveBundle = async (id) => {
    setSavingKey(`b-${id}`);
    try {
      const b = config.bundles[id];
      await axios.put(`${API}/admin/checkout-config/bundle/${id}`, {
        padrao: b.padrao || '',
        conselhos: b.conselhos || '',
        estudantes: b.estudantes || '',
        estudantes_conv: b.estudantes_conv || '',
        soon: !!b.soon,
      });
      toast.success(`Formação ${id} salva.`);
    } catch {
      toast.error('Erro ao salvar formação.');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-red-500" size={32} /></div>;
  }

  const moduleIds = Object.keys(config.modules).map(Number).sort((a, b) => a - b);
  const bundleIds = Object.keys(config.bundles).map(Number).sort((a, b) => a - b);

  const q = search.trim().toLowerCase();
  const matchSearch = (id, title) => !q || (title || '').toLowerCase().includes(q) || String(id).includes(q);

  const filteredModuleIds = moduleIds.filter((id) => {
    const m = config.modules[String(id)];
    const avail = !!m.available;
    const statusOk = statusFilter === 'all' || (statusFilter === 'available' ? avail : !avail);
    return statusOk && matchSearch(id, courseTitles[String(id)]);
  });
  const filteredBundleIds = bundleIds.filter((id) => {
    const b = config.bundles[String(id)];
    const avail = !b.soon;
    const statusOk = statusFilter === 'all' || (statusFilter === 'available' ? avail : !avail);
    return statusOk && matchSearch(id, bundleTitles[String(id)]);
  });

  const FILTERS = [
    { key: 'all', label: 'Todos' },
    { key: 'available', label: 'Disponíveis' },
    { key: 'soon', label: 'Em breve' },
  ];

  return (
    <div className="space-y-8" data-testid="admin-checkout-config">
      <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 text-sm text-blue-100/90">
        Edite aqui os <strong>códigos de oferta da Hotmart</strong> e o <strong>status</strong> de cada módulo e formação.
        O código é a parte final do link (ex.: em <span className="text-blue-300">pay.hotmart.com/<strong>T104249577N</strong></span>, o código é <strong>T104249577N</strong>).
        Quando um item está <strong>Disponível</strong> e tem código, o botão "Comprar Agora" aparece no site apontando para o checkout.
        Itens marcados como <strong>Em breve</strong> exibem o aviso e o botão de WhatsApp.
      </div>

      {/* Filtro / Busca */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou ID..."
            className="bg-zinc-800 border-zinc-600 text-white h-9 pl-9"
            data-testid="checkout-search"
          />
        </div>
        <div className="flex gap-1 bg-zinc-800 rounded-lg p-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${statusFilter === f.key ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-white'}`}
              data-testid={`checkout-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* MÓDULOS */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <ShoppingCart className="text-orange-400" size={20} />
          <h3 className="text-lg font-semibold text-white">Módulos Individuais</h3>
          <Badge className="bg-zinc-700">{filteredModuleIds.length}/{moduleIds.length}</Badge>
        </div>
        <div className="space-y-3">
          {filteredModuleIds.length === 0 && (
            <p className="text-gray-500 text-sm text-center py-4">Nenhum módulo encontrado.</p>
          )}
          {filteredModuleIds.map((id) => {
            const m = config.modules[String(id)];
            const isSaving = savingKey === `m-${id}`;
            return (
              <Card key={id} className="bg-zinc-900 border-zinc-700">
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                    <div className="lg:w-1/3">
                      <div className="text-white font-medium text-sm">{courseTitles[String(id)] || `Curso ${id}`}</div>
                      <div className="text-xs text-gray-500">ID {id}</div>
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-gray-400 block mb-1">Código de oferta Hotmart</label>
                      <Input
                        value={m.offer || ''}
                        onChange={(e) => setModuleField(String(id), 'offer', e.target.value)}
                        placeholder="Ex.: T104249577N"
                        className="bg-zinc-800 border-zinc-600 text-white h-9"
                        data-testid={`checkout-module-offer-${id}`}
                      />
                      {m.offer ? (
                        <div className="text-[11px] text-gray-500 mt-1 truncate">pay.hotmart.com/{m.offer}</div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={!!m.available}
                        onCheckedChange={(v) => setModuleField(String(id), 'available', v)}
                        data-testid={`checkout-module-available-${id}`}
                      />
                      <span className={`text-xs font-semibold ${m.available ? 'text-green-400' : 'text-amber-400'}`}>
                        {m.available ? 'Disponível' : 'Em breve'}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => saveModule(String(id))}
                      disabled={isSaving}
                      className="bg-red-600 hover:bg-red-700 text-white h-9"
                      data-testid={`checkout-module-save-${id}`}
                    >
                      {isSaving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                      <span className="ml-1">Salvar</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* FORMAÇÕES */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Crown className="text-red-400" size={20} />
          <h3 className="text-lg font-semibold text-white">Formações</h3>
          <Badge className="bg-zinc-700">{filteredBundleIds.length}/{bundleIds.length}</Badge>
        </div>
        <div className="space-y-3">
          {filteredBundleIds.length === 0 && (
            <p className="text-gray-500 text-sm text-center py-4">Nenhuma formação encontrada.</p>
          )}
          {filteredBundleIds.map((id) => {
            const b = config.bundles[String(id)];
            const isSaving = savingKey === `b-${id}`;
            const isOpen = expanded[id];
            return (
              <Card key={id} className="bg-zinc-900 border-zinc-700">
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                    <div className="lg:w-1/3">
                      <div className="text-white font-medium text-sm">{bundleTitles[String(id)] || `Formação ${id}`}</div>
                      <div className="text-xs text-gray-500">ID {id}</div>
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-gray-400 block mb-1">Código Padrão (checkout direto)</label>
                      <Input
                        value={b.padrao || ''}
                        onChange={(e) => setBundleField(String(id), 'padrao', e.target.value)}
                        placeholder="Ex.: R106839836G"
                        className="bg-zinc-800 border-zinc-600 text-white h-9"
                        data-testid={`checkout-bundle-padrao-${id}`}
                      />
                      {b.padrao ? (
                        <div className="text-[11px] text-gray-500 mt-1 truncate">pay.hotmart.com/{b.padrao}</div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={!b.soon}
                        onCheckedChange={(v) => setBundleField(String(id), 'soon', !v)}
                        data-testid={`checkout-bundle-soon-${id}`}
                      />
                      <span className={`text-xs font-semibold ${!b.soon ? 'text-green-400' : 'text-amber-400'}`}>
                        {!b.soon ? 'Disponível' : 'Em breve'}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => saveBundle(String(id))}
                      disabled={isSaving}
                      className="bg-red-600 hover:bg-red-700 text-white h-9"
                      data-testid={`checkout-bundle-save-${id}`}
                    >
                      {isSaving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                      <span className="ml-1">Salvar</span>
                    </Button>
                  </div>

                  <button
                    type="button"
                    onClick={() => setExpanded((e) => ({ ...e, [id]: !e[id] }))}
                    className="mt-3 inline-flex items-center gap-1 text-xs text-teal-300 hover:text-teal-200"
                    data-testid={`checkout-bundle-toggle-discounts-${id}`}
                  >
                    {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    Códigos das faixas com desconto (enviados após comprovação de vínculo)
                  </button>

                  {isOpen && (
                    <div className="mt-3 grid md:grid-cols-3 gap-3 border-t border-zinc-700 pt-3">
                      {[
                        { field: 'conselhos', label: 'Conselhos/associações/sindicatos (-50%)' },
                        { field: 'estudantes', label: 'Estudantes (-60%)' },
                        { field: 'estudantes_conv', label: 'Estudantes fac. conveniadas (-70%)' },
                      ].map((tier) => (
                        <div key={tier.field}>
                          <label className="text-xs text-gray-400 block mb-1">{tier.label}</label>
                          <Input
                            value={b[tier.field] || ''}
                            onChange={(e) => setBundleField(String(id), tier.field, e.target.value)}
                            placeholder="Código Hotmart"
                            className="bg-zinc-800 border-zinc-600 text-white h-9"
                            data-testid={`checkout-bundle-${tier.field}-${id}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminCheckoutConfig;
